import React, { useRef, useState, useEffect } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

/**
 * TextOverflow component that shows the full text in a tooltip when it overflows
 *
 * @param {Object} props
 * @param {string} props.text - The text to display
 * @param {string} props.className - Additional classes for the container
 * @param {React.CSSProperties} props.style - Additional styles for the container
 * @param {number} props.maxWidth - Maximum width of the text container
 * @param {string} props.tooltipSide - Position of the tooltip (top, right, bottom, left)
 * @param {React.ReactNode} props.children - Alternative to text prop, children to render
 * @returns {JSX.Element}
 */
export let TextOverflow = ({
  text,
  className = '',
  style = {},
  maxWidth = undefined,
  tooltipSide = 'top',
  children,
  ...props
}: {
  text?: string;
  className?: string;
  style?: React.CSSProperties;
  maxWidth?: number;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLSpanElement>) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentToRender = text || children;

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsOverflowing(
          element.scrollWidth > element.clientWidth ||
            element.scrollHeight > element.clientHeight,
        );
      }
    };

    checkOverflow();

    // Recheck on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [contentToRender]);

  // Combine styles
  const containerStyles = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: maxWidth,
    ...style,
  };

  // If not overflowing, just render the text without a tooltip
  if (!isOverflowing) {
    return (
      <span
        ref={textRef}
        className={className}
        style={containerStyles}
        {...props}
      >
        {contentToRender}
      </span>
    );
  }

  return (
    <Tooltip.Provider>
      <Tooltip.Root
        onOpenChange={(isOpen) => {
          setIsTooltipOpen(isOpen);
        }}
        open={isTooltipOpen}
      >
        <Tooltip.Trigger asChild>
          <span
            ref={textRef}
            {...props}
            className={className}
            style={containerStyles}
            onClick={(e) => {
              props.onClick?.(e);
              setIsTooltipOpen((prev) => !prev);
            }}
          >
            {contentToRender}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side={tooltipSide}
            sideOffset={5}
            className="text-neutral-0 z-50 max-w-xs rounded bg-neutral-800 px-3 py-2 text-sm shadow-md"
          >
            {typeof contentToRender === 'string' ? contentToRender : text}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
